<template>
    <div class="slice barometer-job__experience">
        <div class="slice-content">
            <h2 class="slice-title">{{ title }}</h2>
            <p>{{ subtitle }}</p>
            <div class="barometer-job__experience__charts joy-mt-8">
                <div v-for="(experienceAndPrice, i) in jobBarometer.experienceAndPrices" :key="i">
                    <h3 class="barometer-job__experience__title">
                        {{ t(`barometer.family.average.price.experience.level.${experienceAndPrice.level}`) }}
                    </h3>
                    <div class="barometer-job__experience__chart">
                        <div
                            v-for="(value, j) in experienceAndPrice.priceHistogram.normalizedData"
                            :key="j"
                            class="barometer-job__experience__chart__bar"
                            :style="{height: `${value * 100}%`}"
                            :data-value="`${(value * 100).toFixed(0)}%`"
                        />
                    </div>
                    <!-- eslint-disable vue/no-v-html -->
                    <p class="barometer-job__experience__info">
                        <span
                            aria-hidden="true"
                            v-html="
                                t('barometer.family.average.price.experience.min.price.label', [
                                    experienceAndPrice.priceHistogram.minPrice.toFixed(0),
                                ])
                            "
                        ></span>
                        <span
                            class="txt-info"
                            v-html="t('barometer.family.average.price.experience.average.price.label', [experienceAndPrice.averagePrice.toFixed(0)])"
                        ></span>
                        <span
                            aria-hidden="true"
                            v-html="
                                t('barometer.family.average.price.experience.max.price.label', [
                                    experienceAndPrice.priceHistogram.maxPrice.toFixed(0),
                                ])
                            "
                        ></span>
                    </p>
                    <!-- eslint-enable vue/no-v-html -->
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {inject} from 'vue';
    import {useTranslation} from '#imports';
    import {BAROMETER_JOB} from '~/keys';

    const {jobBarometer} = inject(BAROMETER_JOB)!;
    const {t} = useTranslation();

    const htmlTitle = t('barometer.family.average.price.experience.title');
    const [title, subtitle] = htmlTitle.match(/>([^<]+)</g)?.map((match) => match.slice(1, -1).trim()) || [];
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .barometer-job__experience {
        background-color: var(--joy-color-neutral-20);

        &__title {
            font-family: var(--joy-font-family-title);
            line-height: var(--joy-line-height-small);
            margin-bottom: var(--joy-core-spacing-6);
            font-size: var(--joy-font-size-primary-400);

            @include mq.screen_md(min) {
                font-size: var(--joy-font-size-primary-600);
            }
        }
        &__charts {
            display: grid;
            gap: var(--joy-core-spacing-12);
            max-width: 400px;
            margin: 0 auto;
            grid-template-columns: repeat(1, minmax(0, 1fr));

            @include mq.screen_sm(min) {
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }
            @include mq.screen_sm(min) {
                max-width: 900px;
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            @include mq.screen_lg(min) {
                max-width: none;
                grid-template-columns: repeat(4, minmax(0, 1fr));
            }
        }
        &__chart {
            display: flex;
            align-items: flex-end;
            height: var(--joy-core-spacing-9);
            position: relative;

            &__bar {
                flex: 1;
                width: 100%;
                cursor: pointer;
                position: relative;
                min-height: var(--joy-core-spacing-2);

                &::before {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    right: 0;
                    background-color: var(--joy-color-primary-50);
                    opacity: 0.5;
                    // border-radius: var(--joy-core-radius-2) var(--joy-core-radius-2) 0 0;
                }
                &::after {
                    content: attr(data-value);
                    position: absolute;
                    display: inline-block;
                    width: 32px;
                    left: -6px;
                    bottom: calc(100% + 4px);
                    font-size: var(--joy-font-size-primary-200);
                    pointer-events: none;
                    text-align: center;
                    opacity: 0;
                }

                &:hover {
                    &::before,
                    &::after {
                        opacity: 1;
                    }
                }
            }

            &::before {
                content: '';
                position: absolute;
                width: 100%;
                height: 0.25rem;
                bottom: -0.25rem;
                background-color: var(--joy-color-primary-50);
            }
        }
        &__info {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            margin-top: var(--joy-core-spacing-4);

            > span {
                white-space: nowrap;
                font-size: var(--joy-font-size-primary-300);
                font-weight: var(--joy-font-weight-normal);
            }
        }
    }
</style>
